import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Menu, X } from "lucide-react";
import { LanguageSwitcher } from "../ui/LanguageSwitcher";
import { setLanguagePreference } from "../../i18n";

interface LandingHeaderProps {
  onLoginClick: () => void;
  isAuthenticated?: boolean;
}

const navLinks = [
  { href: "#features", labelKey: "header.navFeatures" },
  { href: "#colaboracao", labelKey: "header.navCollab" },
  { href: "#quadros", labelKey: "header.navBoards" },
  { href: "#how", labelKey: "header.navHow" },
  { href: "#pricing", labelKey: "header.navPricing" },
];

/**
 * Header da landing: transparente sobre o creme do hero e, ao rolar,
 * vira uma pill flutuante com blur.
 */
export function LandingHeader({ onLoginClick, isAuthenticated }: LandingHeaderProps) {
  const { t } = useTranslation("landing");
  const navigate = useNavigate();
  const ctaLabel = isAuthenticated ? t("header.ctaApp") : t("header.ctaLogin");

  // Na landing o idioma é dirigido pela URL (`/` = pt-BR, `/en` = inglês) para
  // SEO: o seletor navega para a rota correspondente. Como aqui a troca parte do
  // usuário, ela também vira preferência e o app segue nesse idioma depois.
  const selectLang = (lng: "pt-BR" | "en") => {
    setLanguagePreference(lng);
    navigate(lng === "en" ? "/en" : "/");
  };
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  // Fechar menu mobile ao rolar
  useEffect(() => {
    if (mobileMenuOpen) setMobileMenuOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrolled]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 ${
        // Recuo lateral só quando o card flutuante aparece, para ele não colar
        // nas bordas no mobile (no desktop o max-w já centraliza com folga).
        scrolled || mobileMenuOpen ? "px-3 sm:px-4 md:px-0" : ""
      }`}
    >
      <div
        className={`mx-auto transition-all duration-300 ${
          scrolled || mobileMenuOpen
            ? "max-w-4xl mt-3 px-5 rounded-3xl md:rounded-full bg-cream/90 backdrop-blur-md border-2 border-ink/10 shadow-lg"
            : "max-w-6xl px-6 bg-transparent border-2 border-transparent"
        }`}
      >
        <div className="h-14 flex items-center justify-between">
          <a href="#" className="flex items-center">
            <img
              src="/logo-taskletto.png"
              alt="Taskletto"
              className="h-7 w-auto"
            />
          </a>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-sm font-semibold text-ink/70 hover:text-ink transition-colors"
              >
                {t(link.labelKey)}
              </a>
            ))}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-2">
            <LanguageSwitcher className="shrink-0" onSelect={selectLang} />
            <button
              id="header-login-btn"
              onClick={onLoginClick}
              className="hidden md:inline-flex items-center gap-2 px-5 py-2 bg-ink text-cream text-sm font-bold rounded-full transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md cursor-pointer"
            >
              {ctaLabel}
              <ArrowRight size={15} />
            </button>

            {/* Mobile hamburger */}
            <button
              className="md:hidden p-2 rounded-full text-ink hover:bg-ink/5 transition-colors"
              onClick={() => setMobileMenuOpen((v) => !v)}
              aria-label={mobileMenuOpen ? t("header.closeMenu") : t("header.openMenu")}
            >
              {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile menu panel */}
        {mobileMenuOpen && (
          <nav className="md:hidden pb-4 pt-1 flex flex-col gap-1 border-t border-ink/10">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-semibold text-ink/80 hover:text-ink hover:bg-ink/5 px-3 py-2.5 rounded-xl transition-colors"
              >
                {t(link.labelKey)}
              </a>
            ))}
            <button
              onClick={() => {
                onLoginClick();
                setMobileMenuOpen(false);
              }}
              className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-ink text-cream text-sm font-bold rounded-full transition-all duration-200 cursor-pointer"
            >
              {ctaLabel}
              <ArrowRight size={15} />
            </button>
          </nav>
        )}
      </div>
    </header>
  );
}
